import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiErr } from "@/lib/api";
import { Compass, ArrowRight, ShieldCheck, Users, Sparkles } from "lucide-react";

const DEMO = [
  { role: "Admin", email: "avishek.roy@inspiroworks.com", password: "Inspiro@2026" },
  { role: "Consultant", email: "abhishek.roy@inspiroworks.com", password: "Consultant@2026" },
  { role: "Customer", email: "rajesh@abcmanufacturing.com", password: "Customer@2026" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiErr(err, "Invalid email or password"));
    } finally {
      setBusy(false);
    }
  };

  const quick = (d) => { setEmail(d.email); setPassword(d.password); };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="brand-gradient relative hidden w-[46%] flex-col justify-between overflow-hidden p-12 lg:flex">
        <div className="absolute inset-0 subtle-grid opacity-20" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
            <Compass className="h-6 w-6 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading text-xl font-extrabold text-white">Inspiro Discover</div>
            <div className="text-xs font-medium text-sky-100">by InspiroWorks</div>
          </div>
        </div>

        <div className="relative max-w-md">
          <h1 className="font-heading text-4xl font-black leading-tight tracking-tight text-white">
            From business requirements to Salesforce blueprint.
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-sky-100">
            A guided discovery workshop that captures what your business needs — in plain language — and turns it into an implementation blueprint your team can act on.
          </p>
          <div className="mt-8 space-y-3">
            {[
              { icon: Sparkles, t: "Educate → Discover → Analyze → Blueprint" },
              { icon: ShieldCheck, t: "Business language first, no Salesforce jargon" },
              { icon: Users, t: "Built for admins, consultants and customers" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3 text-sm font-medium text-white/90">
                <f.icon className="h-4 w-4 text-sky-200" /> {f.t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-sky-100/70">© {new Date().getFullYear()} InspiroWorks · discover.inspiroworks.com</div>
      </div>

      {/* Right form */}
      <div className="flex flex-1 items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Welcome back</h2>
            <p className="mt-1 text-sm text-slate-500">Sign in to continue your discovery.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="mt-1.5 h-12 rounded-lg" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="mt-1.5 h-12 rounded-lg" required />
            </div>
            {error && <div data-testid="login-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{error}</div>}
            <Button type="submit" data-testid="login-submit" disabled={busy}
              className="h-12 w-full rounded-lg bg-sky-600 text-[15px] font-semibold hover:bg-sky-700">
              {busy ? "Signing in…" : <>Sign in <ArrowRight className="ml-1.5 h-4 w-4" /></>}
            </Button>
          </form>

          <div className="mt-8">
            <div className="mb-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-400">Demo accounts</div>
            <div className="grid grid-cols-3 gap-2">
              {DEMO.map((d) => (
                <button key={d.role} data-testid={`demo-${d.role.toLowerCase()}`} onClick={() => quick(d)}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700">
                  {d.role}
                </button>
              ))}
            </div>
            <p className="mt-2 text-center text-[11px] text-slate-400">Tap a role to prefill, then sign in.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
